import { RoomKey } from '../sockets/socket.room.key';
import { EntityRooms } from '../sockets/rooms/entity.rooms';
import { TenantNotificationPayload } from '../sockets/tenant.notification.payload';
import { AccountingResult } from './accounting.result';
import { AccountingStatus, AccountingSystem } from './accounting.enums';

/** Sent to an invoice's or bill's room each time its sync status is written */
export const ACCOUNTING_SYNC_CHANGED_EVENT = 'accounting-sync:changed';

/** The room an open invoice or bill listens on, so its sync status updates without a refresh */
export function accountingSyncRoom(isBill: boolean, documentId: string): RoomKey<TenantNotificationPayload> {

    return isBill ? EntityRooms.bill(documentId) : EntityRooms.invoice(documentId);
}

/**
 * The sync status of an invoice or bill, or null when nothing has been pushed. Results written before the
 * status field existed only carry `success`, so they are read as Processed or Error.
 */
export function accountingSyncStatus(result: Pick<AccountingResult, 'success' | 'status'> | null | undefined): AccountingStatus | null {

    if (result?.status) return result.status;
    if (result?.success == null) return null;

    return result.success ? AccountingStatus.Processed : AccountingStatus.Error;
}

/** Pending or Processing: a worker has a result still to write, so an open document listens for it */
export function isAccountingSyncInFlight(result: Pick<AccountingResult, 'success' | 'status'> | null | undefined): boolean {

    const status = accountingSyncStatus(result);
    return status === AccountingStatus.Pending || status === AccountingStatus.Processing;
}

/** The sync status pill on the invoice and bill details screens */
export const ACCOUNTING_SYNC_STATUS_LABELS: Record<AccountingStatus, string> = {
    [AccountingStatus.Pending]: 'Pending',
    [AccountingStatus.Processing]: 'Processing',
    [AccountingStatus.Processed]: 'Processed',
    [AccountingStatus.Skipped]: 'Skipped',
    [AccountingStatus.Error]: 'Failed',
};

/** How the sync status line names each accounting system; Manual is never shown */
export const ACCOUNTING_SYSTEM_NAMES: Partial<Record<AccountingSystem, string>> = {
    [AccountingSystem.Xero]: 'Xero',
    [AccountingSystem.MYOB]: 'MYOB',
    [AccountingSystem.QuickBooks]: 'QuickBooks',
    [AccountingSystem.MYOBAcumatica]: 'MYOB Acumatica',
    [AccountingSystem.Jobpac]: 'Jobpac',
    [AccountingSystem.ManhattanOne]: 'Manhattan ONE',
};
